import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Divider,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  Paper,
  Collapse,
  alpha,
  Radio,
  RadioGroup,
  FormControlLabel,
  Button,
} from '@mui/material';
import {
  ShoppingCart,
  AttachMoney,
  Image as ImageIcon,
  Inventory2,
  Storefront,
  LocalOffer,
  Schedule,
  SyncAlt,
  Link as LinkIcon,
  Add,
  Receipt,
  RequestQuote,
} from '@mui/icons-material';
import {
  integrationApiService,
  IntegrationConfig,
  IntegrationPlatform,
  IntegrationSettings,
  ShopifySettingLock,
  SyncMode,
} from '../services/integrationApi';
import { INTEGRATION_SETTINGS_COPY as COPY } from '../utils/integrationSettingsCopy';
import { useOrgFeatureFlags } from '../hooks/useOrgFeatureFlags';
import OverrideFieldsPicker, { OVERRIDE_FIELD_ORDER } from './OverrideFieldsPicker';
import ContinueSellingRow from './ContinueSellingRow';
import ShopifyScopeGatedSwitch from './ShopifyScopeGatedSwitch';
import { SETTINGS_ANCHORS } from './dataIngestion/skipReasonActions';
import { useTranslation } from 'react-i18next';
import { useOrgFeatureFlag } from '../hooks/useOrgFeatureFlags';
import { NumberField } from './ui';
import ShopifyTagModeControl from './ShopifyTagModeControl';
import SyncTimeControl from './SyncTimeControl';
import { TrialFeatureTitle } from './trial/TrialFeatureBadge';

/**
 * The settings sections the integration settings drawer (and the setup
 * walkthrough) render. They are controlled: the caller owns the settings
 * object and decides when to save it.
 */

export function normalizeIntegrationSettings(base: IntegrationSettings): IntegrationSettings {
  return {
    ...base,
    fieldMappings: base.fieldMappings ?? [],
    specMetafields: base.specMetafields ?? [],
    addMotzuTag: base.addMotzuTag !== false,
    syncMode: base.syncMode || 'override',
    defaultInventoryCount: base.defaultInventoryCount,
    continueSellingWhenOutOfStock: base.continueSellingWhenOutOfStock === true,
    autoSyncNewProducts: base.autoSyncNewProducts === true,
    allowProPublicSync: base.allowProPublicSync === true,
    syncRefurbishedProducts: base.syncRefurbishedProducts === true,
    syncRentalProducts: base.syncRentalProducts === true,
    groupKitBodyAsVariants: base.groupKitBodyAsVariants !== false,
    // Default on: exclude marketing images unless explicitly disabled
    excludeMarketingImages: base.excludeMarketingImages !== false,
  };
}

export type ShopifyScopeGroup = 'inventory' | 'orders';

/** Shopify permission state the gated switches need: locks, install method and the Grant action. */
export interface ShopifyScopeControls {
  locks?: { syncInventory?: ShopifySettingLock; orderSync?: ShopifySettingLock };
  customApp: boolean;
  /** Plan features this store's plan lacks (from the integrations list). */
  planLocks?: { syncInventory?: boolean; customMetafieldMapping?: boolean };
  grantingGroup: ShopifyScopeGroup | null;
  onGrant: (group: ShopifyScopeGroup) => void;
}

export interface OrderSyncControl {
  enabled: boolean;
  onChange: (value: boolean) => void;
}

/**
 * Grant-in-Shopify state for an integration. Only Motzu Sync (public_oauth) may
 * use Grant in Shopify; a missing installMethod is a pasted token (or custom
 * app), and public OAuth would overwrite the store's Admin API token.
 */
export function useShopifyScopeControls(integration: IntegrationConfig | null): ShopifyScopeControls {
  const [grantingGroup, setGrantingGroup] = useState<ShopifyScopeGroup | null>(null);
  const customApp = integration?.credentials?.installMethod !== 'public_oauth';
  const onGrant = async (group: ShopifyScopeGroup) => {
    if (!integration) return;
    const shop = String(integration.credentials?.shopId || integration.credentials?.shopDomain || '');
    if (!shop || customApp) return;
    setGrantingGroup(group);
    try {
      const data = await integrationApiService.startShopifyInstall(shop, { scopeGroups: [group] });
      window.location.assign(data.installUrl);
    } catch (err) {
      setGrantingGroup(null);
      window.alert(err instanceof Error ? err.message : 'Failed to open Shopify');
    }
  };
  return {
    locks: integration?.scopeStatus?.settingLocks,
    planLocks: integration?.planLocks,
    customApp,
    grantingGroup,
    onGrant,
  };
}

interface SectionBaseProps {
  settings: IntegrationSettings;
  onChange: (partial: Partial<IntegrationSettings>) => void;
  platform: IntegrationPlatform;
}

/**
 * "Pre-order button" (flag `preorder_button`, base): a switch and the message template on
 * the integration. Renders nothing without the flag, or on a platform other than Shopify
 * and BigCommerce.
 */
const PreorderRow: React.FC<SectionBaseProps> = ({ settings, onChange, platform }) => {
  const { enabled: flagOn } = useOrgFeatureFlag('preorder_button');
  const { t } = useTranslation() as { t: (key: string, options?: Record<string, unknown>) => string };
  if (!flagOn || (platform !== 'shopify' && platform !== 'bigcommerce')) return null;
  const preorder = settings.preorder ?? { enabled: false };
  const dateToken = { date: '{date}' };
  return (
    <>
      <Divider />
      <Box sx={{ py: 1.25 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
          <Box sx={{ mr: 1.5, display: 'flex', alignItems: 'center', mt: 0.25 }}>
            <Schedule fontSize="small" color="action" />
          </Box>
          <Box sx={{ flex: 1, mr: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {t('productPreorder.settings.title')}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.45 }}>
              {t('productPreorder.settings.description')}
            </Typography>
          </Box>
          <Switch
            size="small"
            checked={preorder.enabled === true}
            onChange={(e) => onChange({ preorder: { ...preorder, enabled: e.target.checked } })}
            inputProps={{ 'aria-label': t('productPreorder.settings.title') }}
          />
        </Box>
        <Collapse in={preorder.enabled === true} unmountOnExit>
          <TextField
            size="small"
            fullWidth
            sx={{ mt: 1.25 }}
            label={t('productPreorder.settings.messageLabel')}
            placeholder={t('productPreorder.settings.messagePlaceholder', dateToken)}
            helperText={t('productPreorder.settings.messageHelp', dateToken)}
            value={preorder.message ?? ''}
            inputProps={{ maxLength: 200 }}
            onChange={(e) => onChange({ preorder: { ...preorder, enabled: true, message: e.target.value } })}
          />
        </Collapse>
      </Box>
    </>
  );
};

const DEALER_COST_SYNC_PLATFORMS: IntegrationPlatform[] = ['shopify', 'lightspeed', 'bigcommerce'];

/**
 * "Sync dealer cost" (flag `dealer_cost_sync`, base): opt-in write of the store's dealer cost
 * into the platform's merchant-only cost field, with who owns that field. Hidden without the
 * flag or on another platform.
 */
const DealerCostSyncRow: React.FC<SectionBaseProps & { scope?: ShopifyScopeControls }> = ({ settings, onChange, platform, scope }) => {
  const { enabled: flagOn } = useOrgFeatureFlag('dealer_cost_sync');
  const { t } = useTranslation() as { t: (key: string, options?: Record<string, unknown>) => string };
  if (!flagOn || !DEALER_COST_SYNC_PLATFORMS.includes(platform)) return null;
  const value = settings.dealerCostSync ?? { enabled: false };
  const owner = value.owner ?? 'fill_blank';
  const field = t(`dealerCostSync.fields.${platform}`);
  const platformName = t(`dealerCostSync.platforms.${platform}`);
  const inventoryLocked = platform === 'shopify' && scope?.locks?.syncInventory?.locked === true;
  return (
    <>
      <Divider />
      <Box sx={{ py: 1.25 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
          <Box sx={{ mr: 1.5, display: 'flex', alignItems: 'center', mt: 0.25 }}>
            <RequestQuote fontSize="small" color="action" />
          </Box>
          <Box sx={{ flex: 1, mr: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {t('dealerCostSync.title')}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.45 }}>
              {t('dealerCostSync.description', { field, platform: platformName })}
            </Typography>
          </Box>
          <Switch
            size="small"
            checked={value.enabled === true}
            onChange={(e) => onChange({ dealerCostSync: { enabled: e.target.checked, owner } })}
            inputProps={{ 'aria-label': t('dealerCostSync.title') }}
          />
        </Box>
        <Collapse in={value.enabled === true} unmountOnExit>
          <Box sx={{ ml: 5, mt: 1, pl: 1, borderLeft: '2px solid', borderColor: 'primary.light' }}>
            <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
              {t('dealerCostSync.ownerLabel', { field })}
            </Typography>
            <RadioGroup
              value={owner}
              onChange={(e) => onChange({ dealerCostSync: { enabled: true, owner: e.target.value as 'fill_blank' | 'motzu' } })}
            >
              {(['fill_blank', 'motzu'] as const).map((option) => (
                <FormControlLabel
                  key={option}
                  value={option}
                  control={<Radio size="small" />}
                  label={
                    <Box>
                      <Typography variant="body2">
                        {t(option === 'fill_blank' ? 'dealerCostSync.ownerFillBlank' : 'dealerCostSync.ownerMotzu')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.45 }}>
                        {t(option === 'fill_blank' ? 'dealerCostSync.ownerFillBlankHelp' : 'dealerCostSync.ownerMotzuHelp')}
                      </Typography>
                    </Box>
                  }
                  sx={{ alignItems: 'flex-start', mt: 0.5, '& .MuiRadio-root': { pt: 0.25 } }}
                />
              ))}
            </RadioGroup>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, lineHeight: 1.45 }}>
              {t('dealerCostSync.notCleared')}
            </Typography>
            {inventoryLocked && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75, flexWrap: 'wrap' }}>
                <Typography variant="caption" sx={{ color: 'warning.dark', lineHeight: 1.45 }}>
                  {t('dealerCostSync.shopifyScope')}
                </Typography>
                {!scope?.customApp && (
                  <Button size="small" onClick={() => scope?.onGrant('inventory')} disabled={scope?.grantingGroup === 'inventory'}>
                    {COPY.scopeLock.grant}
                  </Button>
                )}
              </Box>
            )}
          </Box>
        </Collapse>
      </Box>
    </>
  );
};

const OrderSyncRow: React.FC<{
  platform: IntegrationPlatform;
  orderSync?: OrderSyncControl;
  scope?: ShopifyScopeControls;
}> = ({ platform, orderSync, scope }) => {
  if (platform !== 'shopify' || !orderSync) return null;
  return (
    <>
      <Divider />
      <Box sx={{ display: 'flex', alignItems: 'flex-start', py: 1.25 }}>
        <Box sx={{ mr: 1.5, display: 'flex', alignItems: 'center', mt: 0.25 }}>
          <Receipt fontSize="small" color="action" />
        </Box>
        <Box sx={{ flex: 1, mr: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {COPY.orderSync.label}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.45 }}>
            {COPY.orderSync.desc}
          </Typography>
        </Box>
        <ShopifyScopeGatedSwitch
          checked={orderSync.enabled}
          onChange={orderSync.onChange}
          lock={scope?.locks?.orderSync}
          customApp={scope?.customApp === true}
          granting={scope?.grantingGroup === 'orders'}
          onGrant={() => scope?.onGrant('orders')}
        />
      </Box>
    </>
  );
};

export interface DataSyncSectionProps extends SectionBaseProps {
  scope?: ShopifyScopeControls;
  /** Shopify only: render the order-sync switch at the end of this section. */
  orderSync?: OrderSyncControl;
}

/** "Data sync": products / pricing / images / inventory, plus the Shopify listing switches. */
export const DataSyncSection: React.FC<DataSyncSectionProps> = ({ settings, onChange, platform, scope, orderSync }) => {
  // Flag `initial_sync_mode`: the starting inventory count stands on its own, outside the inventory
  // sync switch (which is the plan-gated, two-system feature).
  const { isEnabled } = useOrgFeatureFlags();
  const standaloneCount = platform === 'shopify' && isEnabled('initial_sync_mode');
  const lightspeedInventory = platform === 'lightspeed' && isEnabled('lightspeed_catalog_parity') ? COPY.lightspeed.inventory : null;
  const inventoryPlanNotice = scope?.planLocks?.syncInventory ? COPY.planLock.syncInventory : undefined;
  return (
    <Paper variant="outlined" id={SETTINGS_ANCHORS.dataSync} sx={{ scrollMarginTop: 80 }}>
      <Box sx={{ px: 2, py: 1.25, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle2">
          {COPY.dataSync.title}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, lineHeight: 1.45 }}>
          {COPY.dataSync.subtitle}
        </Typography>
      </Box>
      <Box sx={{ px: 2, py: 1 }}>
        {(
          [
            { key: 'syncProducts' as const, label: COPY.syncProducts.label, desc: COPY.syncProducts.desc, icon: <ShoppingCart fontSize="small" color="action" /> },
            { key: 'syncPricing' as const, label: COPY.syncPricing.label, desc: COPY.syncPricing.desc, icon: <AttachMoney fontSize="small" color="action" /> },
            { key: 'syncImages' as const, label: COPY.syncImages.label, desc: COPY.syncImages.desc, icon: <ImageIcon fontSize="small" color="action" /> },
            { key: 'syncInventory' as const, label: COPY.syncInventory.label, desc: COPY.syncInventory.desc, icon: <Inventory2 fontSize="small" color="action" /> },
          ] as const
        ).map(({ key, label, desc, icon }, idx, arr) => (
          <React.Fragment key={key}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', py: 1.25 }}>
              <Box sx={{ mr: 1.5, display: 'flex', alignItems: 'center', mt: 0.25 }}>{icon}</Box>
              <Box sx={{ flex: 1, mr: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {key === 'syncInventory' ? <TrialFeatureTitle title={label} feature="inventorySync" /> : label}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.45 }}>
                  {key === 'syncInventory' && lightspeedInventory ? lightspeedInventory.desc : desc}
                </Typography>
              </Box>
              {key === 'syncInventory' && platform === 'shopify' ? (
                <ShopifyScopeGatedSwitch
                  checked={settings.syncInventory === true}
                  onChange={(checked) => onChange({ syncInventory: checked })}
                  lock={scope?.locks?.syncInventory}
                  planLockedNotice={inventoryPlanNotice}
                  customApp={scope?.customApp === true}
                  granting={scope?.grantingGroup === 'inventory'}
                  onGrant={() => scope?.onGrant('inventory')}
                />
              ) : (
                <Switch
                  size="small"
                  checked={key === 'syncInventory' ? settings.syncInventory === true : settings[key] !== false}
                  onChange={(e) => onChange({ [key]: e.target.checked } as Partial<IntegrationSettings>)}
                />
              )}
            </Box>
            <Collapse in={key === 'syncInventory' && settings.syncInventory === true && !standaloneCount}>
              <Box sx={{ ml: 5, mb: 1.5, pl: 1, borderLeft: '2px solid', borderColor: 'primary.light' }}>
                <NumberField
                  label="Default inventory count"
                  integer
                  min={0}
                  step={1}
                  adornment="units"
                  value={settings.defaultInventoryCount === undefined ? '' : String(settings.defaultInventoryCount)}
                  onChange={(val) => onChange({ defaultInventoryCount: val === '' ? undefined : Number(val) })}
                  helperText={lightspeedInventory ? lightspeedInventory.helper : COPY.defaultInventoryCount.helper}
                  sx={{ width: '100%', maxWidth: 280 }}
                />
              </Box>
            </Collapse>
            <Collapse in={key === 'syncImages' && settings.syncImages !== false}>
              <Box sx={{ ml: 5, mb: 1.5, pl: 1, borderLeft: '2px solid', borderColor: 'primary.light' }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  <Box sx={{ flex: 1, mr: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {COPY.includeMarketingImages.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.45 }}>
                      {COPY.includeMarketingImages.desc}
                    </Typography>
                  </Box>
                  <Switch
                    size="small"
                    checked={settings.excludeMarketingImages === false}
                    onChange={(e) => onChange({ excludeMarketingImages: !e.target.checked })}
                  />
                </Box>
              </Box>
            </Collapse>
            {idx < arr.length - 1 && <Divider />}
          </React.Fragment>
        ))}
        {platform === 'shopify' && (
          <>
            <Divider />
            <Box sx={{ display: 'flex', alignItems: 'flex-start', py: 1.25 }}>
              <Box sx={{ mr: 1.5, display: 'flex', alignItems: 'center', mt: 0.25 }}>
                <Storefront fontSize="small" color="action" />
              </Box>
              <Box sx={{ flex: 1, mr: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {COPY.groupKitBodyAsVariants.label}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.45 }}>
                  {COPY.groupKitBodyAsVariants.desc}
                </Typography>
              </Box>
              <Switch
                size="small"
                checked={settings.groupKitBodyAsVariants !== false}
                onChange={(e) => onChange({ groupKitBodyAsVariants: e.target.checked })}
                inputProps={{ 'aria-label': COPY.groupKitBodyAsVariants.label }}
              />
            </Box>
          </>
        )}
        <PreorderRow settings={settings} onChange={onChange} platform={platform} />
        <DealerCostSyncRow settings={settings} onChange={onChange} platform={platform} scope={scope} />
        {standaloneCount && (
          <>
            <Divider />
            <Box sx={{ py: 1.25 }}>
              <NumberField
                label={COPY.defaultInventoryCount.label}
                integer
                min={0}
                step={1}
                adornment="units"
                value={settings.defaultInventoryCount === undefined ? '' : String(settings.defaultInventoryCount)}
                onChange={(val) => onChange({ defaultInventoryCount: val === '' ? undefined : Number(val) })}
                helperText={COPY.defaultInventoryCount.helperStandalone}
                sx={{ width: '100%', maxWidth: 360 }}
              />
            </Box>
          </>
        )}
        <OrderSyncRow platform={platform} orderSync={orderSync} scope={scope} />
      </Box>
    </Paper>
  );
};

export interface TagsAutomationSectionProps extends SectionBaseProps {
  scope?: ShopifyScopeControls;
  /** Shopify only: render the order-sync switch in this section (the settings drawer's place for it). */
  orderSync?: OrderSyncControl;
  /** Leave out the refurbished and rental switches (the walkthrough asks only the common ones). */
  compact?: boolean;
  /** The store's time zone for the sync time picker (flag `scheduled_sync_time`); `syncTimeZone` on the integration. */
  syncTimeZone?: string | null;
  /** `syncSchedule.lastSyncSlotAt` on the integration: the last slot the scheduler ran. */
  lastSyncSlotAt?: string | null;
}

/** "Tags & automation": Motzu tag, auto sync and interval, new products, refurbished / rentals, order sync. */
export const TagsAutomationSection: React.FC<TagsAutomationSectionProps> = ({
  settings,
  onChange,
  platform,
  scope,
  orderSync,
  compact = false,
  syncTimeZone,
  lastSyncSlotAt,
}) => {
  const { isEnabled } = useOrgFeatureFlags();
  const syncTimeFlag = isEnabled('scheduled_sync_time');
  const motzuTagDesc =
    platform === 'lightspeed' && isEnabled('lightspeed_catalog_parity')
      ? COPY.lightspeed.motzuTag.desc
      : COPY.addMotzuTag.desc;
  return (
    <Paper variant="outlined">
      <Box sx={{ px: 2, py: 1.25, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle2">
          {COPY.tagsAutomation.title}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, lineHeight: 1.45 }}>
          {COPY.tagsAutomation.subtitle}
        </Typography>
      </Box>
      <Box sx={{ px: 2, py: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', py: 1.25 }}>
          <Box sx={{ mr: 1.5, display: 'flex', alignItems: 'center', mt: 0.25 }}>
            <LocalOffer fontSize="small" color="action" />
          </Box>
          <Box sx={{ flex: 1, mr: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {COPY.addMotzuTag.label}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.45 }}>
              {motzuTagDesc}
            </Typography>
          </Box>
          <Switch
            size="small"
            checked={settings.addMotzuTag !== false}
            onChange={(e) => onChange({ addMotzuTag: e.target.checked })}
          />
        </Box>
        {platform === 'shopify' && (
          <ShopifyTagModeControl tagMode={settings.tagMode} onChange={(tagMode) => onChange({ tagMode })} />
        )}
        <Divider />
        <Box sx={{ display: 'flex', alignItems: 'flex-start', py: 1.25 }}>
          <Box sx={{ mr: 1.5, display: 'flex', alignItems: 'center', mt: 0.25 }}>
            <Schedule fontSize="small" color="action" />
          </Box>
          <Box sx={{ flex: 1, mr: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {COPY.autoSync.label}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.45 }}>
              {COPY.autoSync.desc}
            </Typography>
          </Box>
          <Switch
            size="small"
            checked={settings.autoSync}
            onChange={(e) => onChange({ autoSync: e.target.checked })}
          />
        </Box>
        <Collapse in={settings.autoSync}>
          <Box sx={{ ml: 5, mb: 1.5, pl: 1, borderLeft: '2px solid', borderColor: 'primary.light' }}>
            <FormControl size="small" sx={{ width: '100%', maxWidth: 220 }}>
              <InputLabel>Sync interval</InputLabel>
              <Select
                value={settings.syncInterval}
                label="Sync interval"
                onChange={(e) =>
                  onChange({ syncInterval: e.target.value as IntegrationSettings['syncInterval'] })
                }
              >
                <MenuItem value="manual">Manual</MenuItem>
                <MenuItem value="hourly">Hourly</MenuItem>
                <MenuItem value="daily">Daily</MenuItem>
                <MenuItem value="weekly">Weekly</MenuItem>
              </Select>
            </FormControl>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75, lineHeight: 1.4 }}>
              {syncTimeFlag ? COPY.syncTime.intervalHelper : COPY.syncInterval.helper}
            </Typography>
            <SyncTimeControl settings={settings} onChange={onChange} timeZone={syncTimeZone} lastSlotAt={lastSyncSlotAt} />
          </Box>
        </Collapse>
        <Divider />
        <Box sx={{ display: 'flex', alignItems: 'flex-start', py: 1.25 }}>
          <Box sx={{ flex: 1, mr: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {COPY.autoSyncNewProducts.label}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.45 }}>
              {COPY.autoSyncNewProducts.desc}
            </Typography>
          </Box>
          <Switch
            size="small"
            checked={settings.autoSyncNewProducts === true}
            onChange={(e) => onChange({ autoSyncNewProducts: e.target.checked })}
          />
        </Box>
        {!compact && (
          <>
        <Divider />
        <Box id={SETTINGS_ANCHORS.refurbished} sx={{ display: 'flex', alignItems: 'flex-start', py: 1.25, scrollMarginTop: 80 }}>
          <Box sx={{ flex: 1, mr: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {COPY.syncRefurbishedProducts.label}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.45 }}>
              {COPY.syncRefurbishedProducts.desc}
            </Typography>
          </Box>
          <Switch
            size="small"
            checked={settings.syncRefurbishedProducts === true}
            onChange={(e) => onChange({ syncRefurbishedProducts: e.target.checked })}
          />
        </Box>
        <Divider />
        <Box id={SETTINGS_ANCHORS.rental} sx={{ display: 'flex', alignItems: 'flex-start', py: 1.25, scrollMarginTop: 80 }}>
          <Box sx={{ flex: 1, mr: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {COPY.syncRentalProducts.label}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.45 }}>
              {COPY.syncRentalProducts.desc}
            </Typography>
          </Box>
          <Switch
            size="small"
            checked={settings.syncRentalProducts === true}
            onChange={(e) => onChange({ syncRentalProducts: e.target.checked })}
          />
        </Box>
          </>
        )}
        <OrderSyncRow platform={platform} orderSync={orderSync} scope={scope} />
      </Box>
    </Paper>
  );
};

export interface SyncModeSectionProps {
  settings: IntegrationSettings;
  onChange: (partial: Partial<IntegrationSettings>) => void;
  /** Modes to offer. Shopify: all three. Lightspeed: see `lightspeedCatalogModes`. */
  modes?: readonly SyncMode[];
  /** Lightspeed uses Retail POS wording. Shopify is the default. */
  copyVariant?: 'shopify' | 'lightspeed';
  /** With catalog parity, an omitted linkOnlyPricing means prices stay current. */
  linkOnlyPricingDefaultsOn?: boolean;
}

/** "Sync mode": link only / enrich / override, and the override-fields picker. */
export const SyncModeSection: React.FC<SyncModeSectionProps> = ({
  settings,
  onChange,
  modes,
  copyVariant,
  linkOnlyPricingDefaultsOn = false,
}) => {
  const { isEnabled } = useOrgFeatureFlags();
  const lightspeed = copyVariant === 'lightspeed';
  // Enrich in the mode list is also how Shopify is recognized. Lightspeed can offer enrich too.
  const shopifyModes = !lightspeed && (!modes || modes.includes('enrich'));
  // Flag `initial_sync_mode`: Shopify gets a separate choice for products the store does not have yet.
  const initialSyncOn = shopifyModes && isEnabled('initial_sync_mode');
  // Mirrors resolveInitialSyncOptions on the server: nothing stored means Skip under link-only
  // (as it always was) and Create under Enrich and Override.
  const effectiveUnmatchedAction: 'create' | 'skip' =
    settings.unmatchedProductAction === 'create' || settings.unmatchedProductAction === 'skip'
      ? settings.unmatchedProductAction
      : (settings.syncMode || 'override') === 'link_only'
        ? 'skip'
        : 'create';
  const linkOnlyCopy = lightspeed
    ? (linkOnlyPricingDefaultsOn ? COPY.syncMode.lightspeedLinkOnly : COPY.syncMode.linkOnly)
    : (initialSyncOn ? COPY.syncMode.linkOnlyShopifyInitial : COPY.syncMode.linkOnlyShopify);
  const enrichCopy = lightspeed ? COPY.syncMode.lightspeedEnrich : COPY.syncMode.enrich;
  const overrideCopy = lightspeed ? COPY.syncMode.lightspeedOverride : COPY.syncMode.override;
  const linkOnlyPricesOn = linkOnlyPricingDefaultsOn ? settings.linkOnlyPricing !== false : settings.linkOnlyPricing === true;
  const showLinkOnlyPricing =
    settings.syncMode === 'link_only' && (lightspeed || Boolean(modes && !modes.includes('enrich')));
  const showOverrideFields = (settings.syncMode || 'override') === 'override' && (!modes || modes.includes('enrich'));
  return (
      <Paper variant="outlined">
        <Box sx={{ px: 2, py: 1.25, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SyncAlt fontSize="small" color="action" />
            <Typography variant="subtitle2">
              {COPY.syncMode.title}
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, lineHeight: 1.45 }}>
            {lightspeed ? COPY.syncMode.lightspeedSubtitle : COPY.syncMode.subtitle}
          </Typography>
        </Box>
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            {(
              [
                { value: 'link_only' as const, label: linkOnlyCopy.label, desc: linkOnlyCopy.desc, icon: <LinkIcon fontSize="small" /> },
                { value: 'enrich' as const, label: enrichCopy.label, desc: enrichCopy.desc, icon: <Add fontSize="small" /> },
                { value: 'override' as const, label: overrideCopy.label, desc: overrideCopy.desc, icon: <SyncAlt fontSize="small" /> },
              ] as const
            )
              .filter(({ value }) => !modes || modes.includes(value))
              .map(({ value, label, desc, icon }) => {
              const selected = (settings.syncMode || 'override') === value;
              return (
                <Paper
                  key={value}
                  variant="outlined"
                  onClick={() => onChange({ syncMode: value as SyncMode })}
                  sx={{
                    p: 1.5,
                    cursor: 'pointer',
                    borderWidth: selected ? 2 : 1,
                    borderColor: selected ? 'primary.main' : 'divider',
                    bgcolor: selected ? (theme) => alpha(theme.palette.primary.main, 0.04) : 'transparent',
                    transition: 'all 0.15s ease',
                    '&:hover': { borderColor: selected ? 'primary.main' : 'grey.400' },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5, color: selected ? 'primary.main' : 'text.secondary' }}>
                    {icon}
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {label}
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.3 }}>
                    {desc}
                  </Typography>
                </Paper>
              );
            })}
          </Box>
          {initialSyncOn && (
            <Box sx={{ mt: 2 }} data-testid="initial-sync-choice">
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {COPY.syncMode.initialSync.title}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.4, mb: 1 }}>
                {COPY.syncMode.initialSync.subtitle}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {(['create', 'skip'] as const).map((value) => {
                  const selected = effectiveUnmatchedAction === value;
                  const copy = COPY.syncMode.initialSync[value];
                  return (
                    <Paper
                      key={value}
                      variant="outlined"
                      role="radio"
                      aria-checked={selected}
                      tabIndex={0}
                      onClick={() => onChange({ unmatchedProductAction: value })}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onChange({ unmatchedProductAction: value });
                        }
                      }}
                      sx={{
                        p: 1.5,
                        cursor: 'pointer',
                        borderWidth: selected ? 2 : 1,
                        borderColor: selected ? 'primary.main' : 'divider',
                        bgcolor: selected ? (theme) => alpha(theme.palette.primary.main, 0.04) : 'transparent',
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600, color: selected ? 'primary.main' : 'text.primary' }}>
                        {copy.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.3 }}>
                        {copy.desc}
                      </Typography>
                    </Paper>
                  );
                })}
              </Box>
              {effectiveUnmatchedAction === 'skip' && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.4, mt: 1 }}>
                  {COPY.syncMode.initialSync.skipNote}
                </Typography>
              )}
            </Box>
          )}
          {showOverrideFields && (
            <OverrideFieldsPicker
              value={settings.overrideFields}
              onChange={(overrideFields) => onChange({ overrideFields })}
              imagesSyncEnabled={settings.syncImages !== false}
              fields={lightspeed ? OVERRIDE_FIELD_ORDER.filter((field) => field !== 'weight') : undefined}
              copy={
                lightspeed
                  ? {
                      subtitle: COPY.lightspeed.overrideFields.subtitle,
                      keepNote: COPY.lightspeed.overrideFields.keepNote,
                      fields: COPY.lightspeed.overrideFields.fields,
                    }
                  : undefined
              }
            />
          )}
          {shopifyModes && (settings.syncMode || 'override') !== 'link_only' && (
            <Box sx={{ mt: 1.5, pt: 0.5, borderTop: '1px dashed', borderColor: 'divider' }}>
              <ContinueSellingRow
                checked={settings.continueSellingWhenOutOfStock === true}
                onChange={(continueSellingWhenOutOfStock) => onChange({ continueSellingWhenOutOfStock })}
              />
            </Box>
          )}
          {showLinkOnlyPricing && (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mt: 2 }}>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {COPY.syncMode.linkOnlyPricing.label}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.4 }}>
                  {COPY.syncMode.linkOnlyPricing.desc}
                </Typography>
                {linkOnlyPricesOn && settings.syncPricing === false && (
                  <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.5 }}>
                    {COPY.syncMode.linkOnlyPricing.pricingOff}
                  </Typography>
                )}
              </Box>
              <Switch
                size="small"
                checked={linkOnlyPricesOn}
                onChange={(e) => onChange({ linkOnlyPricing: e.target.checked })}
                inputProps={{ 'aria-label': COPY.syncMode.linkOnlyPricing.label }}
              />
            </Box>
          )}
        </Box>
      </Paper>
);
};
